<script lang="ts" setup>
import {computed, reactive, ref} from 'vue'
import {useRouter} from 'vue-router'
import {storeToRefs} from 'pinia'
import i18next from 'i18next'
import markdownit from 'markdown-it'
import {useAppStore} from '@/stores/app'
import {useTelegramStore} from '@/stores/telegram'
import {useLocalStore} from '@/stores/local'
import type {Project, TaskInstance, TaskWithInstance} from '@backend/types'
import {Button} from '@/components/ui/button'
import {Separator} from '@/components/ui/separator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from '@/components/ui/collapsible'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {ToggleGroup, ToggleGroupItem} from '@/components/ui/toggle-group'
import {Badge} from '@/components/ui/badge'
import {Table, TableBody} from '@/components/ui/table'
import TaskItem from '@/components/TaskItem.vue'
import NewTaskDialog from '@/components/NewTaskDialog.vue'
import NewProjectDialog from '@/components/NewProjectDialog.vue'
import {isAgendaTask, isAgendaInstance} from '@/utils/agenda'
import {
  RefreshCw,
  Pencil,
  Trash2,
  Maximize2,
  Plus,
  ChevronDown,
  ChevronRight,
  Clock,
  Hash,
  FilterX,
  Rows2,
  Rows3,
  Rows4,
  Circle,
  CircleDot,
} from 'lucide-vue-next'

const md = markdownit()
const router = useRouter()

const props = defineProps<{
  folder?: string
  projectId?: string | number
  focus?: boolean
  agenda?: boolean
}>()

const appStore = useAppStore()
const {allTasks, projects} = storeToRefs(appStore)

const telegramStore = useTelegramStore()
const {isTelegramWebApp} = storeToRefs(telegramStore)

const localStore = useLocalStore()
const {viewMode, taskFilter} = storeToRefs(localStore)

const deleteDialogOpen = ref(false)
const newTaskOpened = ref(false)
const newProjectOpened = ref(false)
const editProjectOpened = ref(false)
const editingProject = ref<Project | undefined>()
const durationPopoverOpen = ref(false)
const tagPopoverOpen = ref(false)

const collapsedGroups = reactive<Record<string, boolean>>({
  done: true,
  pending: true,
})

const title = computed(() => {
  if (props.agenda) {
    return i18next.t('scopes.agenda')
  }
  if (props.folder) {
    return i18next.t('scopes.' + props.folder)
  }
  if (props.projectId) {
    return currentProject.value?.title ?? i18next.t('scopes.inbox')
  }
  if (props.focus) {
    return i18next.t('scopes.focus')
  }
  return i18next.t('scopes.inbox')
})

const currentProject = computed<Project | undefined>(() => {
  if (props.projectId) {
    return projects.value.find((project) => project.project_id === Number(props.projectId))
  }
  return undefined
})

function isTaskMatch(task: TaskWithInstance) {
  if (props.agenda) {
    return isAgendaTask(task)
  }
  if (props.folder) {
    return task.folder === props.folder
  }
  if (props.projectId) {
    return task.project_id === Number(props.projectId)
  }
  if (props.focus) {
    return task.focused
  }
  return true
}

const groupedTasks = computed(() => {
  const filteredTasks = allTasks.value.filter((task: TaskWithInstance) => isTaskMatch(task))
  const groups: Record<string, {task: TaskWithInstance; taskInstance: TaskInstance | null}[]> = {}

  for (const task of filteredTasks) {
    if (!task.instances.length) {
      const item = {task, taskInstance: null}
      const folder = 'pending'
      if (!groups[folder]) groups[folder] = []
      groups[folder].push(item)
    } else {
      for (const instance of task.instances) {
        if (props.agenda && !isAgendaInstance(instance)) continue
        const item = {task, taskInstance: instance}
        if (instance.done) {
          if (!groups['done']) groups['done'] = []
          groups['done'].push(item)
        } else {
          const folder = task.folder || 'No Folder'
          if (!groups[folder]) groups[folder] = []
          groups[folder].push(item)
        }
      }
    }
  }

  if (props.folder === 'scheduled' || props.agenda) {
    for (const groupName in groups) {
      if (groupName !== 'done') {
        groups[groupName]?.sort((a, b) => {
          const dateA = a.taskInstance?.due_date ? new Date(a.taskInstance.due_date).getTime() : Infinity
          const dateB = b.taskInstance?.due_date ? new Date(b.taskInstance.due_date).getTime() : Infinity
          return dateA - dateB
        })
      }
    }
  } else {
    for (const groupName in groups) {
      if (groupName !== 'done') {
        groups[groupName]?.sort((a, b) => {
          const dateA = new Date(b.task.created_at).getTime()
          const dateB = new Date(a.task.created_at).getTime()
          return dateA - dateB
        })
      }
    }
  }

  return Object.entries(groups)
    .filter(([_, tasks]) => tasks.length > 0)
    .sort(([a], [b]) => {
      if (a === 'done') return 1
      if (b === 'done') return -1
      if (a === 'pending') return 1
      if (b === 'pending') return -1
      return a.localeCompare(b)
    })
})

const availableTags = computed(() => {
  const tagSet = new Set<string>()
  groupedTasks.value.forEach(([_, tasks]) => {
    tasks.forEach(({task}) => {
      task.tags?.forEach((tag) => tagSet.add(tag))
    })
  })
  return Array.from(tagSet).sort()
})

const availableDurations = computed(() => {
  const durations = new Set<number>()
  groupedTasks.value.forEach(([_, tasks]) => {
    tasks.forEach(({task}) => {
      if (task.duration) {
        durations.add(parseInt(task.duration))
      }
    })
  })
  return Array.from(durations).sort((a, b) => a - b)
})

const filteredGroupedTasks = computed(() => {
  return groupedTasks.value
    .map(([groupName, tasks]) => {
      const filtered = tasks.filter(({task}) => {
        if (taskFilter.value.selectedTag && !task.tags?.includes(taskFilter.value.selectedTag)) {
          return false
        }
        if (taskFilter.value.durationFilter) {
          if (!task.duration) return false
          if (parseInt(task.duration) > taskFilter.value.durationFilter) return false
        }
        return true
      })
      return [groupName, filtered] as [string, typeof tasks]
    })
    .filter(([_, tasks]) => tasks.length > 0)
})

const hasActiveFilters = computed(() => {
  return !!taskFilter.value.selectedTag || !!taskFilter.value.durationFilter
})

function resetFilters() {
  taskFilter.value.selectedTag = null
  taskFilter.value.durationFilter = null
}

function selectDuration(duration: number | null) {
  taskFilter.value.durationFilter = duration
  durationPopoverOpen.value = false
}

function selectTag(tag: string | null) {
  taskFilter.value.selectedTag = tag
  tagPopoverOpen.value = false
}

function handleRefresh() {
  appStore.loadAll()
}

function handleEditProject() {
  editingProject.value = currentProject.value
  editProjectOpened.value = true
}

function handleDeleteProject() {
  deleteDialogOpen.value = true
}

async function confirmDeleteProject() {
  if (!props.projectId) return
  await appStore.deleteProject(Number(props.projectId))
  deleteDialogOpen.value = false
  router.push('/')
}

function handleNewTask() {
  newTaskOpened.value = true
}

function handleNewProject() {
  newProjectOpened.value = true
}

function goFullscreen() {
  // @ts-ignore - Telegram WebApp API
  window.Telegram.WebApp.requestFullscreen()
}
</script>

<template>
  <!-- Header: title + action buttons via Teleport -->
  <Teleport to="#page-header">
    <div class="flex flex-1 items-center justify-between">
      <h1 class="text-lg font-semibold truncate">{{ title }}</h1>
      <div class="flex items-center gap-1">
        <Button variant="ghost" size="icon" class="h-8 w-8" @click="handleRefresh">
          <RefreshCw class="h-4 w-4" />
        </Button>
        <template v-if="projectId">
          <Separator orientation="vertical" class="mx-1 h-4" />
          <Button variant="ghost" size="icon" class="h-8 w-8" @click="handleEditProject">
            <Pencil class="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" class="h-8 w-8 text-destructive" @click="handleDeleteProject">
            <Trash2 class="h-4 w-4" />
          </Button>
        </template>
        <Button v-if="isTelegramWebApp" variant="ghost" size="icon" class="h-8 w-8" @click="goFullscreen">
          <Maximize2 class="h-4 w-4" />
        </Button>
      </div>
    </div>
  </Teleport>

  <!-- Main content -->
  <div class="flex flex-1 flex-col">
    <!-- Project details: notes + due date -->
    <div v-if="currentProject" class="mb-4 space-y-2 rounded-lg bg-muted p-4">
      <div v-if="currentProject.notes" class="prose" v-html="md.render(currentProject.notes)" />
      <div v-if="currentProject.due_date" class="text-sm font-medium text-primary">
        {{ $t('fields.due_date') }}: {{ new Date(currentProject.due_date).toLocaleString() }}
      </div>
    </div>

    <Separator />

    <!-- Filter toolbar -->
    <div class="flex items-center justify-between px-2 py-1.5">
      <!-- Left: filters -->
      <div class="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          class="h-7 px-2"
          :class="{'bg-accent': !hasActiveFilters}"
          @click="resetFilters">
          <FilterX class="mr-1 h-3.5 w-3.5" />
          {{ $t('filters.all') }}
        </Button>

        <!-- Duration filter -->
        <Popover v-model:open="durationPopoverOpen">
          <PopoverTrigger as-child>
            <Button variant="ghost" size="sm" class="h-7 px-2" :class="{'bg-accent': !!taskFilter.durationFilter}">
              <Clock class="mr-1 h-3.5 w-3.5" />
              {{ taskFilter.durationFilter ? `≤ ${taskFilter.durationFilter}` : $t('fields.duration') }}
            </Button>
          </PopoverTrigger>
          <PopoverContent class="w-40 p-1" align="start">
            <button
              class="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              @click="selectDuration(null)">
              <Circle v-if="taskFilter.durationFilter" class="h-3.5 w-3.5 text-muted-foreground" />
              <CircleDot v-else class="h-3.5 w-3.5 text-primary" />
              {{ $t('filters.all') }}
            </button>
            <button
              v-for="duration in availableDurations"
              :key="duration"
              class="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              @click="selectDuration(duration)">
              <CircleDot v-if="taskFilter.durationFilter === duration" class="h-3.5 w-3.5 text-primary" />
              <Circle v-else class="h-3.5 w-3.5 text-muted-foreground" />
              ≤ {{ duration }}
            </button>
          </PopoverContent>
        </Popover>

        <!-- Tag filter -->
        <Popover v-model:open="tagPopoverOpen">
          <PopoverTrigger as-child>
            <Button variant="ghost" size="sm" class="h-7 px-2" :class="{'bg-accent': !!taskFilter.selectedTag}">
              <Hash class="mr-1 h-3.5 w-3.5" />
              {{ taskFilter.selectedTag || $t('fields.tags') }}
            </Button>
          </PopoverTrigger>
          <PopoverContent class="w-44 p-1" align="start">
            <button
              class="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              @click="selectTag(null)">
              <Circle v-if="taskFilter.selectedTag" class="h-3.5 w-3.5 text-muted-foreground" />
              <CircleDot v-else class="h-3.5 w-3.5 text-primary" />
              {{ $t('filters.all') }}
            </button>
            <button
              v-for="tag in availableTags"
              :key="tag"
              class="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              @click="selectTag(tag)">
              <CircleDot v-if="taskFilter.selectedTag === tag" class="h-3.5 w-3.5 text-primary" />
              <Circle v-else class="h-3.5 w-3.5 text-muted-foreground" />
              {{ tag }}
            </button>
          </PopoverContent>
        </Popover>
      </div>

      <!-- Right: view mode -->
      <ToggleGroup
        type="single"
        :model-value="viewMode"
        @update:model-value="
          (v: any) => {
            if (v) viewMode = v as typeof viewMode
          }
        ">
        <ToggleGroupItem value="compact" size="sm" class="h-7 w-7 p-0">
          <Rows2 class="h-3.5 w-3.5" />
        </ToggleGroupItem>
        <ToggleGroupItem value="normal" size="sm" class="h-7 w-7 p-0">
          <Rows3 class="h-3.5 w-3.5" />
        </ToggleGroupItem>
        <ToggleGroupItem value="detailed" size="sm" class="h-7 w-7 p-0">
          <Rows4 class="h-3.5 w-3.5" />
        </ToggleGroupItem>
      </ToggleGroup>
    </div>

    <Separator />

    <!-- Task groups -->
    <div class="flex-1">
      <template v-if="filteredGroupedTasks.length > 0">
        <Collapsible
          v-for="[groupName, tasks] in filteredGroupedTasks"
          :key="groupName"
          :open="!collapsedGroups[groupName]"
          @update:open="(val: boolean) => (collapsedGroups[groupName] = !val)"
          class="mb-1">
          <CollapsibleTrigger as-child>
            <button class="flex w-full items-center gap-1.5 px-3 py-2 text-sm font-medium hover:bg-accent/50">
              <ChevronRight v-if="collapsedGroups[groupName]" class="h-4 w-4 shrink-0 text-muted-foreground" />
              <ChevronDown v-else class="h-4 w-4 shrink-0 text-muted-foreground" />
              {{ $t('scopes.' + groupName) }}
              <Badge variant="secondary" class="ml-1 h-5 px-1.5 text-xs">{{ tasks.length }}</Badge>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Table class="table-fixed">
              <colgroup>
                <col class="w-10" />
                <col />
                <col v-if="viewMode !== 'compact'" class="w-24" />
                <col class="w-10" />
              </colgroup>
              <TableBody>
                <TaskItem
                  v-for="item in tasks"
                  :key="item.taskInstance?.task_instance_id ?? item.task.task_id"
                  :task="item.task"
                  :task-instance="item.taskInstance"
                  :view-mode="viewMode" />
              </TableBody>
            </Table>
          </CollapsibleContent>
        </Collapsible>
      </template>

      <div v-else class="flex h-40 items-center justify-center text-muted-foreground">
        {{ $t('messages.no_tasks') }}
      </div>
    </div>

    <!-- Bottom toolbar -->
    <div class="sticky bottom-0 z-10 border-t bg-background p-3">
      <div class="flex gap-2">
        <Button class="flex-1" @click="handleNewTask">
          <Plus class="mr-2 h-4 w-4" />
          {{ $t('actions.new_task') }}
        </Button>
        <Button class="flex-1" variant="outline" @click="handleNewProject">
          <Plus class="mr-2 h-4 w-4" />
          {{ $t('actions.new_project') }}
        </Button>
      </div>
    </div>
  </div>

  <!-- Delete project confirmation dialog -->
  <AlertDialog :open="deleteDialogOpen" @update:open="deleteDialogOpen = $event">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{{ $t('actions.delete_project') }}</AlertDialogTitle>
        <AlertDialogDescription>
          {{ $t('messages.delete_project_confirm') }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>{{ $t('actions.cancel') }}</AlertDialogCancel>
        <AlertDialogAction @click="confirmDeleteProject">{{ $t('actions.delete') }}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <NewTaskDialog
    v-model:open="newTaskOpened"
    :folder="folder"
    :project-id="projectId != null ? String(projectId) : undefined"
    :focus="focus" />

  <NewProjectDialog v-model:open="newProjectOpened" :folder="folder" />
  <NewProjectDialog v-model:open="editProjectOpened" :folder="folder" :project="editingProject" />
</template>
